import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import InfoIcon from "@mui/icons-material/Info";
import LocationIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  parseISO,
} from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Aircraft,
  CsrStatus,
  PlacementWithLocation,
  Trip,
  TripWithTenant,
} from "../../../types";
import {
  SCHEDULE_MAX_DATE,
  SCHEDULE_MIN_DATE,
} from "../../../utils/dateConstants";
import {
  formatDepartureDate,
  isInfinityDate,
} from "../../../utils/dateHelpers";
import { tenantTypeDropdownColors } from "../../../utils/tripTypeHelpers";
import { CsrStatusSelect } from "../../../components/CsrStatusSelect";
import { HangarIcon } from "../../../widgets/Icons/HangarIcon";
import { RampIcon } from "../../../widgets/Icons/RampIcon";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";
import { AssignLocationDialog } from "../AssignLocationDialog/AssignLocationDialog";
import { CreateTripDialog } from "../CreateTripDialog/CreateTripDialog";


interface TripDetailsModalPresenterProps {
  // Modal state
  open: boolean;
  trip: TripWithTenant | null;
  onClose: () => void;

  // Edit state
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;

  // Form state
  editedTrip: Partial<TripWithTenant>;
  setEditedTrip: (trip: Partial<TripWithTenant>) => void;
  selectedAircraft: Aircraft | null;
  setSelectedAircraft: (aircraft: Aircraft | null) => void;

  // Placement state
  placements: PlacementWithLocation[];
  currentLocation: string | null;
  currentLocationType?: "hangar" | "ramp";
  currentTimestamp?: Date;

  // Navigation
  previousTrip: TripWithTenant | null;
  nextTrip: TripWithTenant | null;
  onNavigate?: (tripId: string) => void;

  // Handlers
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  handleCancelTrip: () => Promise<void>;
  handleDeleteTrip: () => Promise<void>;
  handleLocationClick: (placement: PlacementWithLocation) => void;
  handleLocationAssigned: () => Promise<void>;

  // Dialog states
  deleteConfirmOpen: boolean;
  setDeleteConfirmOpen: (open: boolean) => void;
  cancelConfirmOpen: boolean;
  setCancelConfirmOpen: (open: boolean) => void;
  assignLocationOpen: boolean;
  setAssignLocationOpen: (open: boolean) => void;

  // Create trip dialog
  createDialogOpen: boolean;
  setCreateDialogOpen: (open: boolean) => void;
  onTripCreated: (trip: Trip) => void;
  getInitialDatesForNewTrip: () => { arrivalDate: Date; departureDate: Date };

  // CSR Status quick change (saves immediately)
  onCsrStatusChange: (newStatus: CsrStatus) => void;
}

export const TripDetailsModalPresenter: React.FC<
  TripDetailsModalPresenterProps
> = ({
  open,
  trip,
  onClose,
  isEditing,
  setIsEditing,
  loading,
  error,
  setError,
  editedTrip,
  setEditedTrip,
  selectedAircraft,
  setSelectedAircraft,
  placements,
  currentLocation,
  currentLocationType,
  currentTimestamp,
  previousTrip,
  nextTrip,
  onNavigate,
  handleSave,
  handleCancel,
  handleCancelTrip,
  handleDeleteTrip,
  handleLocationClick,
  handleLocationAssigned,
  deleteConfirmOpen,
  setDeleteConfirmOpen,
  cancelConfirmOpen,
  setCancelConfirmOpen,
  assignLocationOpen,
  setAssignLocationOpen,
  createDialogOpen,
  setCreateDialogOpen,
  onTripCreated,
  getInitialDatesForNewTrip,
  onCsrStatusChange,
}) => {
  const { t } = useTranslation();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "primary";
      case "scheduled":
        return "default";
      case "completed":
        return "success";
      case "cancelled":
        return "error";
      case "departed":
        return "warning";
      default:
        return "default";
    }
  };

  const getDisplayStatus = (trip: TripWithTenant): string => {
    // If trip has been explicitly marked as cancelled or completed, show that
    if (trip.status === "cancelled" || trip.status === "completed") {
      return trip.status;
    }

    const now = new Date();
    const arrivalDate = parseISO(trip.arrival_date);

    // Handle infinity departure (base tenants)
    if (isInfinityDate(trip.departure_date)) {
      // If arrival has passed, trip is active (never departs)
      return now >= arrivalDate ? "active" : "scheduled";
    }

    const departureDate = parseISO(trip.departure_date);

    // Compute display status based on current time and trip dates
    if (now >= departureDate) {
      return "departed";
    } else if (now >= arrivalDate && now < departureDate) {
      return "active";
    } else {
      return "scheduled";
    }
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    // Handle infinity end time (base tenants)
    if (isInfinityDate(endTime)) {
      return t("screens.schedule.tripDetailsModal.duration.indefinite");
    }

    const start = parseISO(startTime);
    const end = parseISO(endTime);

    const days = differenceInDays(end, start);
    const hours = differenceInHours(end, start) % 24;
    const minutes = differenceInMinutes(end, start) % 60;

    if (days > 0) {
      return t("screens.schedule.tripDetailsModal.duration.days", { days, hours, minutes });
    } else if (hours > 0) {
      return t("screens.schedule.tripDetailsModal.duration.hours", { hours, minutes });
    } else {
      return t("screens.schedule.tripDetailsModal.duration.minutes", { minutes });
    }
  };

  const getLocationIcon = (locationType: string) => {
    switch (locationType) {
      case "hangar":
        return <HangarIcon />;
      case "ramp":
        return <RampIcon />;
      default:
        return (
          <Tooltip title={t("screens.schedule.tripDetailsModal.tooltips.unassigned")}>
            <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
          </Tooltip>
        );
    }
  };

  if (!trip) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: { xs: "100%", sm: "600px", md: "800px" },
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: "100vh", sm: "90vh" },
            zIndex: 9500,
          },
          elevation: 1,
        }}
        sx={{
          zIndex: 9500,
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">{t("screens.schedule.tripDetailsModal.title")}</Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                {t("screens.schedule.tripDetailsModal.buttons.newTrip")}
              </Button>
              <ToggleButtonGroup
                value={isEditing ? "edit" : "view"}
                exclusive
                onChange={(_, newMode) => {
                  if (newMode && trip.status !== "completed") {
                    setIsEditing(newMode === "edit");
                  }
                }}
                size="small"
                sx={{
                  backgroundColor: "background.paper",
                  boxShadow: isEditing ? 1 : 0,
                  transition: "box-shadow 0.2s ease",
                  "& .MuiToggleButton-root": {
                    px: 2,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 400,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="view">{t("screens.schedule.tripDetailsModal.buttons.view")}</ToggleButton>
                <ToggleButton
                  value="edit"
                  disabled={trip.status === "completed"}
                >
                  <EditIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {t("screens.schedule.tripDetailsModal.buttons.edit")}
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Header Section - Aircraft Info & Status */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {/* Top row: Tail number + badges */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      variant="outlined"
                      label={t("screens.schedule.tripDetailsModal.labels.tailNumber")}
                      value={editedTrip.tail_number || ""}
                      onChange={(e) =>
                        setEditedTrip({
                          ...editedTrip,
                          tail_number: e.target.value.toUpperCase(),
                        })
                      }
                      sx={{ width: 150, bgcolor: 'background.paper' }}
                      inputProps={{ style: { textTransform: "uppercase" } }}
                    />
                  ) : (
                    <Typography variant="h6" fontWeight={600}>
                      {trip.tail_number || t("screens.schedule.tripDetailsModal.placeholders.unknown")}
                    </Typography>
                  )}
                  <Chip
                    label={isEditing ? editedTrip.status : getDisplayStatus(trip)}
                    size="small"
                    color={getStatusColor(
                      isEditing ? editedTrip.status! : getDisplayStatus(trip)
                    )}
                    variant={
                      (isEditing ? editedTrip.status : getDisplayStatus(trip)) ===
                        "active" ||
                      (!isEditing && getDisplayStatus(trip) === "departed")
                        ? "filled"
                        : "outlined"
                    }
                  />
                  {trip.aircraft_type === "transient" && (
                    <Chip label={t("screens.schedule.tripDetailsModal.chips.transient")} size="small" variant="outlined" />
                  )}
                  {/* VIP Tags - show for both base and transient tenants */}
                  {trip.vip_tags?.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.tag_label}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderColor: `${tag.tag_color}60`,
                        color: tag.tag_color,
                        bgcolor: `${tag.tag_color}10`,
                      }}
                    />
                  ))}
                </Stack>

                {/* CSR Status - always editable as a quick action, saves immediately */}
                <CsrStatusSelect
                  value={(editedTrip.csr_status || trip.csr_status || "requested") as CsrStatus}
                  onChange={onCsrStatusChange}
                  disabled={trip.status === "completed" || trip.status === "cancelled"}
                />
              </Stack>
              {isEditing ? (
                <Stack spacing={2} mt={2}>
                  <TextField
                    size="small"
                    variant="outlined"
                    label={t("screens.schedule.tripDetailsModal.labels.ownerManager")}
                    fullWidth
                    value={editedTrip.owner_manager || ""}
                    onChange={(e) =>
                      setEditedTrip({
                        ...editedTrip,
                        owner_manager: e.target.value,
                      })
                    }
                    placeholder={t("screens.schedule.tripDetailsModal.placeholders.ownerManager")}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                  <AutocompleteAircraft
                    label={t("screens.schedule.tripDetailsModal.labels.aircraft")}
                    onSelect={(newValue) => setSelectedAircraft(newValue)}
                    defaultSelected={selectedAircraft || undefined}
                  />
                </Stack>
              ) : (
                <>
                  {trip.owner_manager && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {trip.owner_manager}
                    </Typography>
                  )}
                  {(trip.aircraft_type || trip.aircraft_model) && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {[trip.aircraft_type, trip.aircraft_model]
                        .filter(Boolean)
                        .join(" ")}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            <Divider />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    "& > *": {
                      display: "flex",
                      flexDirection: "column",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1, minHeight: 24 }}
                    >
                      <FlightLandIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("screens.schedule.tripDetailsModal.labels.arrival")}
                      </Typography>
                    </Stack>
                    {isEditing ? (
                      <DateTimePicker
                        value={
                          editedTrip.arrival_date
                            ? new Date(editedTrip.arrival_date)
                            : null
                        }
                        onChange={(value) =>
                          setEditedTrip({
                            ...editedTrip,
                            arrival_date: value?.toISOString(),
                          })
                        }
                        minDate={SCHEDULE_MIN_DATE}
                        maxDate={SCHEDULE_MAX_DATE}
                        ampm={false}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                          popper: {
                            sx: {
                              zIndex: 9700,
                            },
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          backgroundColor: "background.paper",
                        }}
                      >
                        <Typography variant="body2">
                          {format(
                            parseISO(trip.arrival_date),
                            "MM/dd/yyyy HH:mm"
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1, minHeight: 24 }}
                    >
                      <FlightTakeoffIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("screens.schedule.tripDetailsModal.labels.departure")}
                      </Typography>
                    </Stack>
                    {isEditing ? (
                      <DateTimePicker
                        value={
                          editedTrip.departure_date
                            ? new Date(editedTrip.departure_date)
                            : null
                        }
                        onChange={(value) =>
                          setEditedTrip({
                            ...editedTrip,
                            departure_date: value?.toISOString(),
                          })
                        }
                        minDate={SCHEDULE_MIN_DATE}
                        minDateTime={
                          editedTrip.arrival_date
                            ? new Date(editedTrip.arrival_date)
                            : undefined
                        }
                        maxDate={SCHEDULE_MAX_DATE}
                        ampm={false}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                          popper: {
                            sx: {
                              zIndex: 9700,
                            },
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          backgroundColor: "background.paper",
                        }}
                      >
                        <Typography variant="body2">
                          {formatDepartureDate(trip.departure_date)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ flex: 0.6, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1, minHeight: 24 }}
                    >
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("screens.schedule.tripDetailsModal.labels.duration")}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {isEditing
                          ? editedTrip.arrival_date && editedTrip.departure_date
                            ? calculateDuration(
                                editedTrip.arrival_date,
                                editedTrip.departure_date
                              )
                            : "—"
                          : calculateDuration(
                              trip.arrival_date,
                              trip.departure_date
                            )}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("screens.schedule.tripDetailsModal.labels.notes")}
                  </Typography>
                  {isEditing ? (
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      value={editedTrip.notes || ""}
                      onChange={(e) =>
                        setEditedTrip({ ...editedTrip, notes: e.target.value })
                      }
                      placeholder={t("screens.schedule.tripDetailsModal.placeholders.notes")}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color={trip.notes ? "text.primary" : "text.secondary"}
                    >
                      {trip.notes || t("screens.schedule.tripDetailsModal.placeholders.noNotes")}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </LocalizationProvider>

            {!isEditing && (
              <>
                <Divider />

                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("screens.schedule.tripDetailsModal.labels.locationHistory")}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LocationIcon />}
                      onClick={() => setAssignLocationOpen(true)}
                      disabled={
                        trip.status === "completed" ||
                        trip.status === "cancelled"
                      }
                    >
                      {t("screens.schedule.tripDetailsModal.buttons.assignToLocation")}
                    </Button>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("screens.schedule.tripDetailsModal.tableHeaders.location")}</TableCell>
                          <TableCell>{t("screens.schedule.tripDetailsModal.tableHeaders.arrival")}</TableCell>
                          <TableCell>{t("screens.schedule.tripDetailsModal.tableHeaders.departure")}</TableCell>
                          <TableCell align="right">{t("screens.schedule.tripDetailsModal.tableHeaders.duration")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const consolidatedPlacements = (() => {
                            if (!placements.length) return [];

                            const consolidated: PlacementWithLocation[] = [];
                            let current = { ...placements[0] };

                            for (let i = 1; i < placements.length; i++) {
                              const next = placements[i];

                              if (
                                current.hangar_id === next.hangar_id &&
                                current.ramp_id === next.ramp_id &&
                                current.location_name === next.location_name
                              ) {
                                current = {
                                  ...current,
                                  end_time: next.end_time,
                                };
                              } else {
                                consolidated.push(current);
                                current = { ...next };
                              }
                            }

                            consolidated.push(current);

                            return consolidated;
                          })();

                          return consolidatedPlacements.map((placement) => {
                            const isCurrentPlacement = currentTimestamp
                              ? new Date(placement.start_time) <=
                                  currentTimestamp &&
                                (isInfinityDate(placement.end_time) || new Date(placement.end_time) > currentTimestamp)
                              : false;

                            return (
                              <TableRow
                                key={placement.id}
                                sx={{
                                  backgroundColor: isCurrentPlacement
                                    ? "rgba(25, 118, 210, 0.05)"
                                    : "inherit",
                                  borderLeft: isCurrentPlacement
                                    ? "4px solid"
                                    : "none",
                                  borderLeftColor: "primary.main",
                                }}
                              >
                                <TableCell>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    {getLocationIcon(placement.location_type)}
                                    {placement.location_name &&
                                    placement.location_name !== "Unassigned" ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "primary.main",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          "&:hover": {
                                            textDecoration: "none",
                                            backgroundColor: "action.hover",
                                            borderRadius: 1,
                                            px: 0.5,
                                          },
                                        }}
                                        onClick={() =>
                                          handleLocationClick(placement)
                                        }
                                      >
                                        {placement.location_name}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {t("screens.schedule.tripDetailsModal.placeholders.unassigned")}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {format(
                                      parseISO(placement.start_time),
                                      "MMM d 'at' HH:mm"
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {isInfinityDate(placement.end_time)
                                      ? "Not scheduled"
                                      : format(
                                          parseISO(placement.end_time),
                                          "MMM d 'at' HH:mm"
                                        )}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  <Typography variant="body2">
                                    {calculateDuration(
                                      placement.start_time,
                                      placement.end_time
                                    )}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("screens.schedule.tripDetailsModal.labels.tripNavigation")}
                  </Typography>
                  {!previousTrip && !nextTrip ? (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{ py: 1 }}
                    >
                      {t("screens.schedule.tripDetailsModal.navigation.noOtherTrips")}
                    </Typography>
                  ) : (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ flex: 1 }}>
                        {previousTrip && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => onNavigate?.(previousTrip.trip_id)}
                            sx={{ justifyContent: "flex-start" }}
                            fullWidth
                          >
                            {t("screens.schedule.tripDetailsModal.navigation.previous", {
                              dates: `${format(parseISO(previousTrip.arrival_date), "MMM d")} - ${
                                isInfinityDate(previousTrip.departure_date)
                                  ? "∞"
                                  : format(parseISO(previousTrip.departure_date), "MMM d")
                              }`
                            })}
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {nextTrip && (
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => onNavigate?.(nextTrip.trip_id)}
                            sx={{ justifyContent: "flex-start" }}
                            fullWidth
                          >
                            {t("screens.schedule.tripDetailsModal.navigation.next", {
                              dates: `${format(parseISO(nextTrip.arrival_date), "MMM d")} - ${
                                isInfinityDate(nextTrip.departure_date)
                                  ? "∞"
                                  : format(parseISO(nextTrip.departure_date), "MMM d")
                              }`
                            })}
                          </Button>
                        )}
                      </Box>
                    </Stack>
                  )}
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, mr: "auto" }}>
            {trip.status !== "completed" && trip.status !== "cancelled" && (
              <>
                <Button
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelConfirmOpen(true)}
                  disabled={loading}
                  color="warning"
                  size="small"
                >
                  {t("screens.schedule.tripDetailsModal.buttons.cancelTrip")}
                </Button>
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={loading}
                  color="error"
                  size="small"
                >
                  {t("screens.schedule.tripDetailsModal.buttons.deleteTrip")}
                </Button>
              </>
            )}
          </Box>

          {isEditing ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={handleCancel} disabled={loading}>
                {t("screens.schedule.tripDetailsModal.buttons.cancel")}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={handleSave}
                disabled={loading}
                sx={{
                  minWidth: 100,
                }}
              >
                {loading ? t("screens.schedule.tripDetailsModal.buttons.saving") : t("screens.schedule.tripDetailsModal.buttons.save")}
              </Button>
            </Box>
          ) : (
            <Button onClick={onClose} size="small">
              {t("screens.schedule.tripDetailsModal.buttons.close")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        aria-labelledby="delete-confirmation-title"
        sx={{
          zIndex: 9600,
        }}
      >
        <DialogTitle id="delete-confirmation-title">{t("screens.schedule.tripDetailsModal.confirmations.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("screens.schedule.tripDetailsModal.confirmations.deleteMessage", { tailNumber: trip?.tail_number })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={loading}
          >
            {t("screens.schedule.tripDetailsModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleDeleteTrip}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? t("screens.schedule.tripDetailsModal.buttons.deleting") : t("screens.schedule.tripDetailsModal.buttons.deleteTrip")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="sm"
        aria-labelledby="cancel-confirmation-title"
        sx={{
          zIndex: 9600,
        }}
      >
        <DialogTitle id="cancel-confirmation-title">{t("screens.schedule.tripDetailsModal.confirmations.cancelTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("screens.schedule.tripDetailsModal.confirmations.cancelMessage", { tailNumber: trip?.tail_number })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            disabled={loading}
          >
            {t("screens.schedule.tripDetailsModal.buttons.keepTrip")}
          </Button>
          <Button
            onClick={handleCancelTrip}
            color="warning"
            variant="contained"
            disabled={loading}
          >
            {loading ? t("screens.schedule.tripDetailsModal.buttons.cancelling") : t("screens.schedule.tripDetailsModal.buttons.cancelTrip")}
          </Button>
        </DialogActions>
      </Dialog>

      <AssignLocationDialog
        open={assignLocationOpen}
        trip={trip}
        currentLocation={currentLocation}
        onClose={() => setAssignLocationOpen(false)}
        onAssign={handleLocationAssigned}
      />

      {/* Create Trip Dialog - use simple mode for existing tenants */}
      {trip && (
        <CreateTripDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={onTripCreated}
          initialArrivalDate={getInitialDatesForNewTrip().arrivalDate}
          initialDepartureDate={getInitialDatesForNewTrip().departureDate}
          currentLocationId={currentLocation || undefined}
          currentLocationType={currentLocationType}
          simpleMode={true}
          existingTenant={trip}
        />
      )}
    </>
  );
};
